import { DOCUMENT } from '@angular/common';
import { Injectable, RendererFactory2, inject } from '@angular/core';

import { OnFeatureInit } from '@frontend/vanilla/core';
import { PLATFORM } from '@frontend/vanilla/ssr';
import { firstValueFrom } from 'rxjs';

import { GeoCoordinatorClientConfig } from './client-config';
import { BootstrapAsset } from './client-models/coordinator.models';
import { TextReplacementService } from './text-replacement.service';

@Injectable({
    providedIn: 'root',
})
export class GeoCoordinatorBootstrapService implements OnFeatureInit {
    private readonly platform = inject(PLATFORM);
    private readonly documentRef = inject(DOCUMENT);
    private readonly rendererFactory = inject(RendererFactory2);
    private readonly renderer = this.rendererFactory.createRenderer(null, null);
    private readonly geoConfig = inject(GeoCoordinatorClientConfig);
    private readonly textReplacementService = inject(TextReplacementService);

    async onFeatureInit() {
        await this.bootstrap();
    }

    async bootstrap() {
        await firstValueFrom(this.geoConfig.whenReady);

        if (this.geoConfig.isEnabled) {
            this.bootstrapAssets();
        }
    }

    buildPluginLoadUrl() {
        return this.textReplacementService.replace(
            Object.values(this.geoConfig.bootstrapAssets).find((assetConfig) => assetConfig?.uniqueAlias?.trim()?.toLowerCase() === 'pluginurl')
                ?.url ?? '',
        );
    }

    private bootstrapAssets() {
        const assets = this.geoConfig.bootstrapAssets;
        Object.entries(assets)
            .filter(([assetIdentifier, _]) => !this.isAlreadyLoaded(assetIdentifier))
            .forEach(([assetIdentifier, assetConfig]) => this.bootstrapAsset(assetIdentifier, assetConfig));
    }

    private bootstrapAsset(assetIdentifier: string, assetConfig: BootstrapAsset) {
        let assetSrc = (assetConfig.url ?? '').trim();
        let asModule = assetConfig.asModule ?? false;
        let scriptBody = (assetConfig.scriptBody ?? '').trim();
        let identifier = (assetIdentifier ?? '').trim();
        let loadDelay = this.platform.isBrowser ? (assetConfig.loadDelay ?? 0) : 0;

        const scriptId = generateHash(`${assetSrc}|${identifier}|${scriptBody}`);
        // eslint-disable-next-line unicorn/prefer-query-selector
        if (this.documentRef.getElementById(scriptId)) {
            return;
        }
        const script = this.renderer.createElement('script');
        this.renderer.setAttribute(script, 'id', scriptId);

        if (identifier) {
            this.renderer.setAttribute(script, 'data-asset-identifier', identifier);
        }

        if (assetSrc) {
            this.renderer.setAttribute(script, 'type', assetSrc.endsWith('.js') ? 'application/javascript' : 'text/javascript');
            this.renderer.setAttribute(script, 'src', this.textReplacementService.replace(assetSrc));
        }

        if (asModule) {
            this.renderer.setAttribute(script, 'type', 'module');
        }

        if (scriptBody) {
            this.renderer.setProperty(script, 'textContent', scriptBody);
        }

        setTimeout(() => this.renderer.appendChild(this.documentRef.body, script), loadDelay);
    }

    private isAlreadyLoaded(assetIdentifier: string) {
        let identifier = (assetIdentifier ?? '').trim();
        return identifier && this.documentRef.body.querySelector(`[data-asset-identifier="${identifier}"]`) && true;
    }
}

/**
 * A method that returns a hash representation of a string using a variant of DJB2 hash
 * algorithm.
 *
 * This is the same hashing logic that is used to generate component ids.
 */
function generateHash(value: string): string {
    let hash = 0;

    for (const char of value) {
        hash = Math.trunc(Math.imul(31, hash) + char.charCodeAt(0));
    }

    // Force positive number hash.
    // 2147483647 = equivalent of Integer.MAX_VALUE.
    hash += 2_147_483_647 + 1;

    return hash.toString();
}
